import { SiteUrls } from '@tableau/shared-urls';
import * as _ from 'underscore';
import { ServiceLocator } from '../../app/ServiceLocator';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { WindowLocationService } from '../services/WindowLocationService';
import { Projects } from '../../projects/resources/Projects';
import { Views } from '../../views/resources/Views';
import { ISiteSettingsForServerAdmin } from '../../settings/types/ISite';
import { ITagWithCount } from '../../ts/directives/tbTagsInput';
import { ILocLinkifierModel } from '../../ts/filters/LocLinkifierFilter';
import { IActionContext } from '../../ts/helpers/ActionContext';
import { Filter } from '../../ts/helpers/Filter';
import { SpaceInfoItemTypeValue } from '../../ts/models/ContentType';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IDataRoleBase } from '../../ts/models/IDataRoleInfo';
import { IDataSourceBase, IDataSourceDetailInfo } from '../../ts/models/IDataSourceInfo';
import { ILensBase } from '../../ts/models/ILensInfo';
import { IProjectBase, IProjectDetailInfo, IProjectInfo } from '../../ts/models/IProjectInfo';
import { ISliceable } from '../../ts/models/ISliceable';
import { IViewBase, IViewDetailInfo } from '../../ts/models/IViewInfo';
import { IWorkbookBase, IWorkbookDetailInfo, IWorkbookInfo } from '../../ts/models/IWorkbookInfo';
import { IErrorItem } from './MultipleItemResult';
import {
  IDownloadItemInfo,
  IEditableItemInfo,
  INamedItemInfo,
  IOwnedItemInfo,
  ISpaceTypeInfo,
  ITabbedItemInfo,
  ITaggedItemInfo,
} from '../../ts/models/resourceInfoParts';
import { Set } from '../../ts/models/Set';
import { Workbooks } from '../../content/resources/Workbooks';
import { DataSources } from '../../content/resources/Datasources';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { IUserName } from '../../ts/ServerApi/types/IUser';
import { ActionHelpers } from './ActionHelpers';
import { ChangeContentOwnerAction } from '../../ts/services/ChangeContentOwnerAction';
import { ChangeContentTagsAction } from '../../ts/services/ChangeContentTagsAction';
import { ChangeDatasourceRemoteQueryAgentAction } from '../../ts/services/ChangeDatasourceRemoteQueryAgentAction';
import { ChangeDatasourcesTagsAction } from '../../ts/services/ChangeDatasourcesTagsAction';
import { ChangeViewsTagsAction } from '../../ts/services/ChangeViewTagsAction';
import { ChangeWorkbooksTagsAction } from '../../ts/services/ChangeWorkbooksTagsAction';
import { CreateProjectAction } from '../../ts/services/CreateProjectAction';
import { DeleteContentsAction } from '../../ts/services/DeleteContentsAction';
import { DeleteSitesAction } from '../../ts/services/DeleteSitesAction';
import { EditSiteAction } from '../../ts/services/EditSiteAction';
import { MoveToProjectAction } from '../../ts/services/MoveToProjectAction';
import { RenameAction } from '../../ts/services/RenameAction';
import { SetDisplayTabsAction } from '../../ts/services/SetDisplayTabsAction';

export interface IConfirmDialogTypeaheadAttributes<T> {
  model: T;
  displayField: string;
  suggestions: (matchText: string) => ISliceable<any>;
  matchPrefixesOnly?: boolean;
}

export interface IConfirmDialogTypeaheadScope<T> extends ng.IScope {
  description: string;
  typeahead: IConfirmDialogTypeaheadAttributes<T>;
}

export interface IConfirmChangeOwnerScope extends ng.IScope {
  ownerPicker: {
    prompt: string;
    ownerQuery: (query: string) => ISliceable<IUserName>;
    targetOwner: IUserName;
  };
}

export interface IConfirmChangeWorkbookOwnerScope extends ng.IScope {
  ownerPicker: {
    prompt: string;
    promptDescriptionLinkifierModel: () => ILocLinkifierModel;
    ownerQuery: (query: string) => ISliceable<IUserName>;
    targetOwner: IUserName;
  };
}

export interface IConfirmDialogTagsScope extends ng.IScope {
  description: string;
  tags: {
    model: ITagWithCount[];
  };
}

export interface IDeleteAction {
  (set: Set<any>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IDownloadAction {
  (downloadItem: IDownloadItemInfo): any;
}

export interface IChangeOwnerAction {
  (set: Set<INamedItemInfo & IOwnedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IUpdateSiteAvailabilityAction {
  (set: Set<ISiteSettingsForServerAdmin>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IEditSiteAction {
  (site: ISiteSettingsForServerAdmin, actionContext: IActionContext): ng.IPromise<any>;
}

export interface IEditWorkbookAction {
  (item: IEditableItemInfo): any;
}

export interface IChangeTagsAction {
  (set: Set<ITaggedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

export interface ISetDisplayTabsAction {
  (set: Set<ITabbedItemInfo>, actionContext: IActionContext): ng.IPromise<any>;
}

const getMoveToProjectAction = () => ServiceLocator.get<MoveToProjectAction>('MoveToProjectAction');
const getCreateProjectAction = () => ServiceLocator.get<CreateProjectAction>('CreateProjectAction');
const getChangeContentTagsAction = () => ServiceLocator.get<ChangeContentTagsAction>('ChangeContentTagsAction');
const getChangeViewsTagsAction = () => ServiceLocator.get<ChangeViewsTagsAction>('ChangeViewsTagsAction');
const getChangeWorkbooksTagsAction = () => ServiceLocator.get<ChangeWorkbooksTagsAction>('ChangeWorkbooksTagsAction');
const getChangeDatasourcesTagsAction = () => ServiceLocator.get<ChangeDatasourcesTagsAction>('ChangeDatasourcesTagsAction');
const getDeleteSitesAction = () => ServiceLocator.get<DeleteSitesAction>('DeleteSitesAction');
const getEditSiteAction = () => ServiceLocator.get<EditSiteAction>('EditSiteAction');
const getChangeDatasourceRemoteQueryAgentAction = () => ServiceLocator.get<ChangeDatasourceRemoteQueryAgentAction>('ChangeDatasourceRemoteQueryAgentAction');
const getSetDisplayTabsAction = () => ServiceLocator.get<SetDisplayTabsAction>('SetDisplayTabsAction');
const getChangeContentOwnerAction = () => ServiceLocator.get<ChangeContentOwnerAction>('ChangeContentOwnerAction');
const getDeleteContentsAction = () => ServiceLocator.get<DeleteContentsAction>('DeleteContentsAction');

class ContentActions {

  // Delete actions

  public deleteProject(projectDetail: IProjectDetailInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return Projects.all()
      .withFilters([Filter.fieldEq('id', projectDetail.id)])
      .getFirst()
      .then((projectInfo: IProjectInfo) => {
        return this.deleteProjects(this.singleton(projectInfo), actionContext);
      });
  }

  public deleteProjects(projects: Set<IProjectInfo>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getDeleteContentsAction().confirmDeleteContents(projects.toArray(), actionContext);
  }

  public deleteWorkbook(workbookDetail: IWorkbookDetailInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return Workbooks.all()
      .withFilters([Filter.fieldEq('id', workbookDetail.id)])
      .getFirst()
      .then((workbookInfo: IWorkbookInfo) => {
        return this.deleteWorkbooks(this.singleton(workbookInfo), actionContext);
      });
  }

  public deleteWorkbooks(workbooks: Set<IWorkbookInfo>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getDeleteContentsAction().confirmDeleteContents(workbooks.toArray(), actionContext);
  }

  // Move to Project

  public moveProjectsToProject(projects: Set<IProjectBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getMoveToProjectAction().moveProjects(projects, actionContext);
  }

  public moveWorkbooksToProject(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getMoveToProjectAction().moveWorkbooks(workbooks, actionContext);
  }

  public moveDatasourcesToProject(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getMoveToProjectAction().moveDatasources(datasources, actionContext);
  }

  public moveDataRolesToProject(dataRoles: Set<IDataRoleBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getMoveToProjectAction().moveDataRoles(dataRoles, actionContext);
  }

  public moveLensesToProject(lenses: Set<ILensBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getMoveToProjectAction().moveLenses(lenses, actionContext);
  }

  // Create project
  // Returns the new project id
  public createProject(actionContext: IActionContext, parentId?: string): ng.IPromise<IResult> {
    return getCreateProjectAction().execute(actionContext, parentId);
  }

  // Helpers

  private singleton<T extends INamedItemInfo>(item: T) {
    return Set.singleton(item, (item: T) => item.id);
  }

  // Rename

  public renameProjectPopup(project: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameProject(project);
  }

  public renameWorkbookPopup(workbook: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameWorkbook(workbook);
  }

  public renameViewPopup(view: INamedItemInfo): ng.IPromise<IResult> {
    return RenameAction.renameView(view);
  }

  // Download

  public changeOwnerOnDatasources(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeContentOwnerAction().confirmChangeOwner(datasources.toArray(), actionContext);
  }

  public changeOwnerOnProjects(projects: Set<IProjectBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeContentOwnerAction().confirmChangeOwner(projects.toArray(), actionContext);
  }

  public changeOwnerOnWorkbooks(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeContentOwnerAction().confirmChangeOwner(workbooks.toArray(), actionContext);
  }

  // Edit Site Settings

  public editSite(site: ISiteSettingsForServerAdmin, actionContext: IActionContext): ng.IPromise<void> {
    return getEditSiteAction().execute(site, actionContext);
  }

  // Delete Sites

  public deleteSites(sites: Set<ISiteSettingsForServerAdmin>, actionContext: IActionContext): ng.IPromise<any> {
    return getDeleteSitesAction().execute(sites, actionContext);
  }

  // Authoring

  private authoring(authoringUrl: string) {
    WindowLocationService.open(authoringUrl);
  }

  private generateUniqueAuxiliaryId() {
    const base36Digit = () => {
      const s = '0123456789abcdefghijklmnopqrstuvwxyz';
      return s[Math.floor(Math.random() * s.length)];
    };

    const b36 = (len: number) => {
      let s = '';
      for (let i = 0; i < len; ++i) {
        s += base36Digit();
      }
      return s;
    };

    // To generate the auxiliary ID, use a base-32 encoding of the client time-stamp plus a guid-like base-36 pseudo-unique identifier.
    // We use half the number of digits compared to a standard GUID since base-36 has over twice the number of encoding bits.
    // A collision of identifiers generated in this way is virtually impossible.
    return (new Date()).getTime().toString(32).toLowerCase() + '$' + b36(4) + '-'  + b36(2) + '-' + b36(2) + '-' + b36(2) + '-' + b36(6);
  }

  public editWorkbook(workbook: IWorkbookDetailInfo) {
    this.authoring(workbook.editUrl);
  }

  public editView(view: IViewDetailInfo) {
    this.authoring(view.editUrl);
  }

  public newFlow(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    this.authoring(this.generateNewFlowUrl(siteUrlName, spaceItemInfo, projectId));
  }

  public newPublishedConnection(projectLuid?: string) {
    this.authoring(this.publishedConnectionEditorUrlForNewPublishedConnection(projectLuid));
  }

  public editPublishedConnection(publishedConnectionId: string) {
    this.authoring(this.publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId));
  }

  private publishedConnectionEditorUrlForNewPublishedConnection(projectLuid?: string) {
    if (projectLuid) {
      return `${this.publishedConnectionEditorPrefix}?project=${projectLuid}`;
    } else {
      return this.publishedConnectionEditorPrefix;
    }
  }

  private publishedConnectionEditorUrlForExistingPublishedConnection(publishedConnectionId: string) {
    return `${this.publishedConnectionEditorPrefix}?published-connection=${publishedConnectionId}`;
  }

  private readonly publishedConnectionEditorPrefix = '/published-connection-editor/';

  public editFlow(siteUrlName: string, flowLuid: string) {
    this.authoring(this.generateEditFlowUrl(siteUrlName, flowLuid));
  }

  public editUnpublishedFlowDraft(urls: SpacesSiteUrls, draftLuid: string) {
    this.authoring(urls.flowEditorDraftUrl(draftLuid));
  }

  public generateNewFlowUrl(siteUrlName: string, spaceItemInfo: ISpaceTypeInfo, projectId?: string) {
    const urlSearchParams = new URLSearchParams();
    if (spaceItemInfo.contentType === SpaceInfoItemTypeValue.datasource) {
      urlSearchParams.set('datasource', (spaceItemInfo as IDataSourceDetailInfo).urlName);
    }
    if (projectId) {
      urlSearchParams.set('projectId', projectId);
    }
    const urlSearchTerm: string = urlSearchParams.toString();
    const siteUrl = new SiteUrls({ siteUrlName });
    const flowLuid = 'new';

    return `${siteUrl.flowEditorFlowPath({ flowLuid: 'new' })}${urlSearchTerm ? `?${urlSearchTerm}` : ''}`;
  }

  public generateEditFlowUrl(siteUrlName: string, flowLuid: string) {
    const siteUrl = new SiteUrls({ siteUrlName });
    return siteUrl.flowEditorFlowPath({ flowLuid: flowLuid });
  }

  public newEmptyWorkbook(siteName: string, projectName?: string) {
    this.authoring(this.generateNewWorkbookUrl('newWorkbook', siteName, projectName));
  }

  public generateNewWorkbookFromDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    return `${this.generateNewWorkbookUrl('authoringNewWorkbook', siteName)}/${datasource.urlName}`;
  }

  public generateNewWorkbookUrl(newWorkbookUrlName: string, siteName: string, projectName?: string) {
    return (siteName === '' ? '' : '/t/' + siteName) +
      '/' + newWorkbookUrlName + '/' +
      (!projectName || projectName === '' ? '' : 'p/' + projectName + '/') +
      this.generateUniqueAuxiliaryId();
  }

  public newMetric(datasource: IDataSourceDetailInfo, siteName: string) {
    WindowLocationService.open(this.generateNewMetricForDatasourceUrl(datasource, siteName));
  }

  public generateNewMetricForDatasourceUrl(datasource: IDataSourceDetailInfo, siteName: string) {
    let site = siteName === '' ? '' : '/t/' + siteName;
    return `${site}/metrics/new/${this.generateUniqueAuxiliaryId()}?:ds=${datasource.urlName}&:dsId=${datasource.id}`;
  }

  // Change Tags

  public setTagsOnViews(views: Set<IViewBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeViewsTagsAction().execute(views, actionContext);
  }

  public setTagsOnView(view: IViewBase, newTagsList: string[]): ng.IPromise<IResult> {
    const tagsToAdd = _.difference(newTagsList, view.tags);
    const tagsToRemove = _.difference(view.tags, newTagsList);

    return Views.changeTags([view.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([view]),
        result,
        ChangeViewsTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IViewBase>) => getChangeViewsTagsAction().changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbook(workbook: IWorkbookBase, newTagsList: string[]): ng.IPromise<IResult> {
    const tagsToAdd = _.difference(newTagsList, workbook.tags);
    const tagsToRemove = _.difference(workbook.tags, newTagsList);

    return Workbooks.changeTags([workbook.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([workbook]),
        result,
        ChangeWorkbooksTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IWorkbookBase>) => getChangeWorkbooksTagsAction().changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnWorkbooks(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeWorkbooksTagsAction().execute(workbooks, actionContext);
  }

  public setTagsOnDatasource(datasource: IDataSourceDetailInfo, newTagsList: string[]): Promise<IResult> {
    const tagsToAdd = _.difference(newTagsList, datasource.tags);
    const tagsToRemove = _.difference(datasource.tags, newTagsList);

    return DataSources.changeTags([datasource.id], tagsToAdd, tagsToRemove)
      .then(result => ActionHelpers.displayStandardToastsForResult(
        Set.fromIdItemArray([datasource]),
        result,
        ChangeDatasourcesTagsAction.notificationTranslationFns,
        (errorItem: IErrorItem<IDataSourceBase>) => getChangeDatasourcesTagsAction().changeTagsErrorDetail(errorItem)
      ));
  }

  public setTagsOnDatasources(datasources: Set<IDataSourceBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeDatasourcesTagsAction().showChangeTagsDialog(datasources, actionContext);
  }

  public setTagsOnMixedContent(content: IContentInfo[], actionContext: IActionContext): ng.IPromise<IResult> {
    return getChangeContentTagsAction().showChangeTagsForMixedContentDialog(content, actionContext);
  }

  // Set Display Tabs

  public setDisplayTabsOnWorkbook(workbooks: Set<IWorkbookBase>, actionContext: IActionContext): ng.IPromise<IResult> {
    return getSetDisplayTabsAction().confirmSetDisplayTabs(workbooks, actionContext);
  }

  public changeRemoteQueryAgentOnDatasource(datasource: IDataSourceDetailInfo): ng.IPromise<IResult> {
    return getChangeDatasourceRemoteQueryAgentAction().execute(datasource);
  }

  public openWhoHasSeenThisView(viewId: string) {
    const whoHasSeenThisViewLink = '/vizql/showadminview/views/WhoHasSeen?views_id=' + viewId;
    WindowLocationService.open(whoHasSeenThisViewLink);
  }
}

export const ContentActionsService = new ContentActions();
// export only the type of the class, since we don't want modules to be able to instantiate it
export type { ContentActions };
